import { useEffect, useState, useCallback } from "react";
import { fetchPromotions, fetchPromotion } from "@/lib/promotions";
import { PageHeader } from "@/components/PageHeader";
import { Tag } from "@/components/Tag";
import { PromotionEditor } from "@/components/PromotionEditor";
import { PromotionDetail } from "@/components/PromotionDetail";
import { Plus, Pencil, Eye } from "lucide-react";

function fmtMonth(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export default function PromotionFocusPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPromotions();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("indielaw:sync-complete", handler);
    return () => window.removeEventListener("indielaw:sync-complete", handler);
  }, [load]);

  const openDetail = async (id) => {
    const p = await fetchPromotion(id);
    setDetail(p);
  };

  return (
    <div data-testid="promotion-focus-page">
      <PageHeader
        eyebrow="Monthly tracker"
        title="Promotion Focus"
        subtitle="One promotion at a time. Set the headline belief, link the supporting beliefs, write the CTA, then build content around it."
        testId="promotion-focus-header"
        actions={
          <button
            onClick={() => setCreating(true)}
            data-testid="new-promotion-button"
            className="inline-flex items-center gap-2 bg-[#141B4D] text-[#F2E9DB] px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90"
          >
            <Plus size={14} strokeWidth={2.5} />
            New promotion
          </button>
        }
      />

      <div className="mb-3 text-xs text-[#141B4D]/60" data-testid="promotion-focus-count">
        {loading ? "Loading…" : `${items.length} promotions`}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-[#141B4D]">
              <th className="py-3 pr-4 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Month
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[28%]">
                Promotion
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Offer Tag
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[30%]">
                Headline Belief
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Status
              </th>
              <th className="py-3 pl-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[1%]"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const headline = p.headline_belief;
              return (
                <tr
                  key={p.id}
                  data-testid={`promotion-row-${p.id}`}
                  className="border-b border-[#141B4D]/15 hover:bg-[#141B4D]/5 transition-colors"
                >
                  <td className="py-4 pr-4 align-top text-sm text-[#141B4D] whitespace-nowrap">
                    {fmtMonth(p.month)}
                  </td>
                  <td className="py-4 px-3 align-top">
                    <button
                      onClick={() => openDetail(p.id)}
                      data-testid={`promotion-open-${p.id}`}
                      className="text-sm text-[#141B4D] font-medium text-left hover:underline decoration-[#FFD100] decoration-2 underline-offset-4"
                    >
                      {p.promotion_name}
                    </button>
                    {p.cta && (
                      <div className="text-xs text-[#141B4D]/55 mt-1">{p.cta}</div>
                    )}
                  </td>
                  <td className="py-4 px-3 align-top">
                    {p.offer_tag ? <Tag>{p.offer_tag}</Tag> : "—"}
                  </td>
                  <td className="py-4 px-3 align-top">
                    {headline ? (
                      <div className="text-xs text-[#141B4D]/85 leading-relaxed line-clamp-2">
                        {headline.belief_text}
                      </div>
                    ) : (
                      <span className="text-xs text-[#141B4D]/40">—</span>
                    )}
                  </td>
                  <td className="py-4 px-3 align-top">
                    <Tag
                      variant={
                        p.status === "Active"
                          ? "active"
                          : p.status === "Complete"
                            ? "retired"
                            : "default"
                      }
                    >
                      {p.status}
                    </Tag>
                  </td>
                  <td className="py-4 pl-3 align-top whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openDetail(p.id)}
                        data-testid={`promotion-view-${p.id}`}
                        className="p-1.5 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm"
                        title="View"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => setEditing(p)}
                        data-testid={`promotion-edit-${p.id}`}
                        className="p-1.5 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-[#141B4D]/50">
                  No promotions yet. Hit "New promotion" to set one up — or
                  sync from your Google Sheet once the Integrations card is
                  configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PromotionEditor
        open={creating || !!editing}
        promotion={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => load()}
      />

      <PromotionDetail
        open={!!detail}
        promotion={detail}
        onClose={() => setDetail(null)}
        onEdit={() => {
          if (detail) {
            setEditing(detail);
            setDetail(null);
          }
        }}
      />
    </div>
  );
}
